import {createMultiStyleConfigHelpers, useMultiStyleConfig} from '@chakra-ui/react';
import {mode} from '@chakra-ui/theme-tools';

import {semantic as dark} from '../../design-tokens/dark';
import {core, semantic} from '../../design-tokens/light';
import {getCoreValueAsThemeToken} from '../types';

const parts = [
  `wrapper`,
  `addFilterButton`,
  `resetFiltersButton`,
  `groupTitle`,
  `groupTitleBackButton`,
  `filterMenuGroup`,
  `filterMenuItem`,
  `filterMenuItemOption`,
  `filterCtaMenuItem`,
  `cancelButton`,
  `saveFilterButton`,
  `searchGroup`,
  `searchRightElement`,
  `searchInput`,
  `filterButton`,
  `appliedFilterBadge`,
  `appliedFilterMenuButton`,
  `removeFilterButton`,
  `appliedFilterName`,
  `appliedFilterValue`,
] as const;
export type TableFiltersParts = (typeof parts)[number];

const {definePartsStyle, defineMultiStyleConfig} = createMultiStyleConfigHelpers(parts);

const baseStyle = definePartsStyle({
  // TableFilters.tsx
  wrapper: {
    borderRadius: `md`,
    spacing: `8`,
  },
  addFilterButton: {
    // baseStyle: {
    //   background: `transparent`,
    //   color: `blackAlpha.600`,
    //   // TODO make the dashes more spaced out:
    //   // https://stackoverflow.com/a/18064496/1288913
    //   border: `1px dashed`,
    //   borderColor: `blackAlpha.600`,
    //   padding: `5px 16px`,
    //   marginY: `1px`,
    //   borderRadius: `md`,
    //   lineHeight: `base`,
    //   fontSize: `md`,
    //   fontWeight: `500`,
    // },
  },
  resetFiltersButton: {
    backgroundColor: `blackAlpha.50`,
    color: `blackAlpha.700`,
    padding: `6px 16px`,
    height: `auto`,
    borderRadius: `md`,
    fontSize: `md`,
    fontWeight: `semibold`,
    _hover: {
      backgroundColor: `blackAlpha.200`,
    },
  },
  // FilterMenu.tsx
  groupTitle: {
    fontSize: `sm`,
    padding: `space-100`,
    gap: `space-100`,
  },
  groupTitleBackButton: {
    color: `blackAlpha.500`,
    marginRight: `4`,
    height: `auto`,
    minHeight: `16`,
    width: `auto`,
    minWidth: `16`,
    fontSize: `sm`,
    _hover: {
      backgroundColor: `blackAlpha.100`,
      color: `blackAlpha.700`,
    },
  },
  filterMenuGroup: {
    margin: 0,
    paddingX: `8px`,
    paddingTop: `6px`,
  },
  filterMenuItem: {
    fontSize: `md`,
    margin: 0,
    paddingLeft: `8`,
    paddingTop: `8`,
    paddingRight: `8`,
    paddingBottom: `8`,
    _hover: {
      backgroundColor: `unset`,
    },
    _active: {
      backgroundColor: `unset`,
    },
  },
  filterMenuItemOption: {
    fontSize: `md`,
  },
  searchGroup: {
    height: `auto`,
    paddingBlock: `8px`,
  },
  searchRightElement: {
    width: `auto`,
    padding: `8px 0 8px 12px`,
    marginTop: `9px`,
  },
  searchInput: {
    width: `14rem`,
    fontSize: `sm`,
    padding: `16px 6px`,
    borderRadius: `md`,
    borderColor: `border`,
    flexGrow: 1,
    _hover: {
      borderColor: `brand.light`,
    },
  },
  filterButton: {
    height: `26px`,
    paddingX: `12px`,
  },
  filterCtaMenuItem: {
    fontSize: `md`,
    margin: 0,
    padding: `space-100`,
    pt: 0,
    justifyContent: `end`,
    _hover: {
      backgroundColor: `unset`,
    },
    _active: {
      backgroundColor: `unset`,
    },
  },
  cancelButton: {},
  saveFilterButton: {
    color: `brand.light`,
  },
  // AppliedFilters.tsx
  appliedFilterBadge: {
    background: `background.body`,
    color: `blackAlpha.800`,
    borderRadius: `md`,
    padding: 0,
    maxW: `full`,
  },
  appliedFilterMenuButton: {
    span: {
      display: `flex`,
      alignItems: `center`,
      flexWrap: `wrap`,
      lineHeight: 1.2,
    },
    padding: `9px 16px`,
    marginRight: `-1px`,
    borderWidth: 0,
    borderTopLeftRadius: `radii.${getCoreValueAsThemeToken(core, `radii.radius-100`)}`,
    borderBottomLeftRadius: `radii.${getCoreValueAsThemeToken(core, `radii.radius-100`)}`,
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    boxShadow: `md`,
    fontSize: `lg`,
    lineHeight: `1.5rem`,
    _hover: {
      backgroundColor: `blackAlpha.200`,
    },
  },
  removeFilterButton: {
    shadow: `sm`,
    color: `blackAlpha.500`,
    minWidth: `6`,
    padding: `6px 8px`,
    borderWidth: 0,
    borderTopLeftRadius: `0`,
    borderBottomLeftRadius: `0`,
    borderLeft: `1px solid`,
    borderLeftColor: `stroke.primary`,
    _hover: {
      color: `blackAlpha.700`,
      backgroundColor: `blackAlpha.200`,
    },
  },
  appliedFilterName: {
    fontSize: `sm`,
    fontWeight: `normal`,
    lineHeight: `inherit`,
  },
  appliedFilterValue: {
    fontWeight: `bold`,
    lineHeight: `inherit`,
    textOverflow: `ellipsis`,
    overflowX: `hidden`,
  },
});

const TableFiltersTheme = defineMultiStyleConfig({
  baseStyle,
  variants: {
    // TODO: separate baseStyle from variants
    primary: {},
    secondary: definePartsStyle(props => {
      return {
        addFilterButton: {},
        removeFilterButton: {
          backgroundColor: mode(semantic.colors.background.primary, dark.colors.background.primary)(props),
          color: mode(semantic.colors.icon.secondary, dark.colors.icon.secondary)(props),
        },
        appliedFilterName: {
          color: mode(semantic.colors.text.secondary, dark.colors.text.secondary)(props),
        },
        appliedFilterMenuButton: {
          backgroundColor: mode(semantic.colors.background.primary, dark.colors.background.primary)(props),
          _focus: {
            backgroundColor: mode(semantic.colors.background.secondary, dark.colors.background.secondary)(props),
          },
          _active: {
            backgroundColor: mode(semantic.colors.background.secondary, dark.colors.background.secondary)(props),
          },
        },
        appliedFilterValue: {
          color: mode(semantic.colors.text[`brand-bold`], dark.colors.text[`brand-bold`])(props),
        },
      };
    }),
  },
  defaultProps: {
    variant: `primary`,
  },
});

export default TableFiltersTheme;

export const useTableFiltersStyleConfig = (
  part: TableFiltersParts,
  props?: Parameters<typeof useMultiStyleConfig>[1],
) => {
  return useMultiStyleConfig(`TableFilters`, props ?? {})?.[part] ?? {};
};
